// YERİ: src/app/domains/site/sections/hero.section.ts
// Hero banner — tema-sürümlü (CSS vars), config-okur. Tüm sektörlerde kullanılır;
// "avukat görünümü" tema (lacivert+serif) + preset içeriğinden gelir.

import { Component, computed, input } from '@angular/core';
import { SiteConfig, cfg } from './section.model';

@Component({
  selector: 'sec-hero',
  standalone: true,
  template: `
    <section id="hero" class="relative overflow-hidden"
             [style.background-color]="heroImage() ? null : 'var(--site-primary-dark)'">
      @if (mediaType() === 'video' && videoUrl()) {
        <video [src]="videoUrl()!" autoplay muted loop playsinline
               class="absolute inset-0 h-full w-full object-cover"></video>
        <div class="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25"></div>
      } @else if (heroImage(); as img) {
        <img [src]="img" alt="" class="absolute inset-0 h-full w-full object-cover" />
        <div class="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25"></div>
      } @else {
        <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--site-primary)_0%,_var(--site-primary-dark)_60%)]"></div>
        <!-- Desen temadan gelir: aynı hero düzeni farklı desenle başka bir
             tasarım gibi okunuyor. Koşul YOK — tema desen vermediğinde
             --site-pattern 'none' olur ve katman kendiliğinden boş kalır.
             (Koşullu çizmek için getComputedStyle okumak gerekirdi; SSR'de
             document yok, orada patlardı.) -->
        <div class="ws-pattern-light ws-pattern-fade absolute inset-0"></div>
      }

      <div class="relative mx-auto max-w-7xl px-6 py-28 sm:py-36 lg:px-8">
        <div class="max-w-3xl">
          @if (badge()) {
            <span class="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-white/90 backdrop-blur">
              <span class="h-1.5 w-1.5 rounded-full" style="background-color:var(--site-accent)"></span>
              {{ badge() }}
            </span>
          }
          <h1 class="font-[family-name:var(--site-head-font)] text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {{ headline() }}
            @if (highlight()) {
              <span class="block" style="color:var(--site-accent)">{{ highlight() }}</span>
            }
          </h1>
          @if (subtext()) {
            <p class="mt-6 max-w-2xl text-lg leading-relaxed text-white/80">{{ subtext() }}</p>
          }
          <div class="mt-10 flex flex-col gap-3 sm:flex-row">
            @if (primaryCtaText()) {
              <a [href]="primaryCtaUrl()"
                 class="ws-btn inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition hover:opacity-90"
                 [class]="fxClass()"
                 style="background-color:var(--site-accent)">
                {{ primaryCtaText() }}
              </a>
            }
            @if (secondaryCtaText()) {
              <a [href]="secondaryCtaUrl()"
                 class="ws-btn inline-flex items-center justify-center border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition hover:bg-white/10"
                 [class]="fxClass()">
                {{ secondaryCtaText() }}
              </a>
            }
            @for (b of buttons(); track $index) {
              <a [href]="b.url || '#'" [target]="isNewTab(b) ? '_blank' : '_self'" [attr.rel]="isNewTab(b) ? 'noopener' : null"
                 class="ws-btn inline-flex items-center justify-center rounded-lg px-7 py-3.5 text-sm font-semibold transition hover:opacity-90"
                 [class.text-white]="b.variant !== 'outline'"
                 [style.background-color]="b.variant === 'outline' ? 'transparent' : 'var(--site-accent)'"
                 [style.color]="b.variant === 'outline' ? '#fff' : null"
                 [style.border]="b.variant === 'outline' ? '1px solid rgba(255,255,255,0.3)' : null">
                {{ b.text }}
              </a>
            }
          </div>

          @if (mediaType() === 'gallery' && galleryImages().length > 1) {
            <div class="mt-10 flex flex-wrap gap-3">
              @for (img of galleryImages().slice(0, 5); track $index) {
                <div class="size-16 overflow-hidden rounded-xl ring-2 ring-white/40 sm:size-20">
                  <img [src]="img.url" [alt]="img.caption || ''" loading="lazy" class="h-full w-full object-cover" />
                </div>
              }
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class HeroSection {
  config = input<SiteConfig>({});
  protected badge = computed(() => cfg(this.config(), 'badge', ''));
  protected headline = computed(() => cfg(this.config(), 'headline', 'Hukukta Güvenilir Çözüm Ortağınız'));
  protected highlight = computed(() => cfg(this.config(), 'highlight', ''));
  protected subtext = computed(() => cfg(this.config(), 'subtext', 'Deneyimli avukat kadromuzla bireysel ve kurumsal müvekkillerimize stratejik hukuki danışmanlık ve güçlü temsil sunuyoruz.'));
  protected primaryCtaText = computed(() => cfg(this.config(), 'primaryCtaText', 'Ücretsiz Görüşme'));
  protected primaryCtaUrl = computed(() => cfg(this.config(), 'primaryCtaUrl', '#contact'));
  protected secondaryCtaText = computed(() => cfg(this.config(), 'secondaryCtaText', 'Uygulama Alanları'));
  protected secondaryCtaUrl = computed(() => cfg(this.config(), 'secondaryCtaUrl', '#practice'));
  protected bgImage = computed(() => cfg<string | null>(this.config(), 'imageUrl', null));

  /** Buton efekti (editörden seçilir): lift | shine | pulse. */
  protected fxClass = computed(() => {
    const fx = String(cfg(this.config(), 'buttonEffect', ''));
    return fx ? 'ws-fx-' + fx : '';
  });

  // Medya: image (tek görsel) | video (arka plan videosu) | gallery (galeri görselleri).
  protected mediaType = computed(() => cfg(this.config(), 'mediaType', 'image'));
  protected videoUrl = computed(() => cfg<string | null>(this.config(), 'videoUrl', null));
  protected galleryImages = computed<{ url: string; caption?: string }[]>(() => cfg(this.config(), 'images', []));
  /** Ek butonlar (primary/secondary'den sonra). */
  protected buttons = computed<{ text: string; url: string; variant?: string; newTab?: string }[]>(() => {
    const raw = this.config()['buttons'];
    return Array.isArray(raw) ? raw.filter(b => b?.text) : [];
  });
  protected isNewTab(b: { newTab?: string }) { return ['true', 'evet', '1'].includes(String(b?.newTab ?? '').toLowerCase()); }
  /** Arka plan görseli: galeri modunda ilk görsel, değilse tekil imageUrl. */
  protected heroImage = computed<string | null>(() =>
    this.mediaType() === 'gallery' ? (this.galleryImages()[0]?.url ?? null) : this.bgImage());
}
